import React, { useState, useEffect } from 'react';
import { FiCheck, FiFileText, FiCreditCard, FiX, FiClock, FiDollarSign } from "react-icons/fi";
import axios from 'axios';
import { BASE_URL, GET_BILL_HISTORY } from '../utlilities/config';

const Spinner = () => (
  <div className="fixed inset-0 z-[2100] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      <p className="text-indigo-600 font-medium">Loading...</p>
    </div>
  </div>
);

const PaymentModal = ({ 
    showPaymentModal, 
    setShowPaymentModal, 
    selectedBills, 
    allBills, 
    paymentAmounts, 
    setPaymentAmounts, 
    paymentMethod, 
    setPaymentMethod, 
    calculateTotal, 
    calculatePaymentTotal, 
    updatePaymentAmount, 
    handlePayment,
    printAfterSave,
    setPrintAfterSave,
    printBillAfterSave,
    setPrintBillAfterSave
  }) => {
    const [cashReceived, setCashReceived] = useState(0);
    const [loading, setLoading] = useState(false);
    const [billsWithHistory, setBillsWithHistory] = useState({});
    const token = localStorage.getItem('accessToken');
    
    const paymentMethods = [
      { value: 'Cash', label: 'Cash Payment', icon: '💵', color: 'bg-green-50 border-green-200 text-green-700' },
      { value: 'UPI', label: 'UPI Payment', icon: '📱', color: 'bg-blue-50 border-blue-200 text-blue-700' },
      { value: 'Card', label: 'Card Payment', icon: '💳', color: 'bg-purple-50 border-purple-200 text-purple-700' },
    ];

    const calculateBalance = () => {
      return cashReceived - calculatePaymentTotal();
    };

    // Reset cash received when modal closes or payment method changes
    useEffect(() => {
      if (paymentMethod !== 'Cash') {
        setCashReceived(0);
      }
    }, [paymentMethod]);

    // Fetch receipt history for selected bills when modal opens
    useEffect(() => {
      if (showPaymentModal && selectedBills.length > 0) {
        fetchReceiptHistoryForBills();
      }
    }, [showPaymentModal, selectedBills]);

    // Helper function to format date and time
    const formatDateTime = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    };

    // Helper function to get payment method badge color
    const getPaymentBadgeColor = (payMode) => {
      switch (payMode) {
        case 'Cash':
          return 'bg-green-100 text-green-800';
        case 'UPI':
          return 'bg-blue-100 text-blue-800';
        case 'Card':
          return 'bg-purple-100 text-purple-800';
        default:
          return 'bg-blue-100 text-gray-800';
      }
    };

    // Fetch receipt history for all selected bills
    const fetchReceiptHistoryForBills = async () => {
      try {
        setLoading(true);
        const historyPromises = selectedBills.map(async (billId) => {
          const bill = allBills.find(b => b.id === billId);
          if (!bill) return null;

          try {
            const response = await axios.get(`${BASE_URL}${GET_BILL_HISTORY}`, {
              headers: {
                'XApiKey': process.env.REACT_APP_API_KEY,
                'Authorization': `Bearer ${token}`,
                'InvoiceNo': bill.invoiceNo.toString(),
                'Sequence': bill.sequence.toString(),
                'TransType': bill.transType,
              },
            });

            if (response.status === 200 && response.data.data) {
              return {
                billId: billId,
                history: response.data.data
              };
            }
            return { billId: billId, history: [] };
          } catch (error) {
            console.error(`Error fetching history for bill ${billId}:`, error);
            return { billId: billId, history: [] };
          }
        });

        const results = await Promise.all(historyPromises);
        
        // Create a map of billId to receipt history
        const historyMap = {};
        results.forEach(result => {
          if (result) {
            historyMap[result.billId] = result.history;
          }
        });
        
        setBillsWithHistory(historyMap);
      } catch (error) {
        console.error('Error fetching receipt histories:', error);
      } finally {
        setLoading(false);
      }
    };

    // Get receipt history for a specific bill
    const getReceiptHistory = (billId) => {
      return billsWithHistory[billId] || [];
    };
  
    return (
      <>
        {loading && <Spinner />}
        <div className="fixed inset-0 z-[1100]  bg-black bg-opacity-60 flex items-center justify-center z-1000">
          <div className="bg-white  rounded-lg shadow-xl max-w-7xl w-full mx-4 max-h-[95vh] overflow-hidden flex flex-col">
            {/* Header - Compact */}
            <div className="bg-white p-3 border-b border-gray-300">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-black flex items-center">
                    <FiCreditCard className="mr-2" size={18} />
                    Payment Processing
                  </h2>
                  <p className="text-xs text-black mt-0.5">{selectedBills.length} bill(s) selected</p>
                </div>
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    setPaymentAmounts({});
                    setCashReceived(0);
                    setBillsWithHistory({});
                  }}
                  className="text-gray-600 hover:text-gray-800 transition-colors p-1.5 hover:bg-gray-100 rounded"
                >
                  <FiX size={20} />
                </button>
              </div>
            </div>
    
            <div className="p-3 overflow-y-auto flex-1">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                
                {/* Left Column - Payment Methods & Summary */}
                <div className="lg:col-span-1 space-y-3">
                  {/* Payment Method */}
                  <div className="bg-gray-100 rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center gap-2 pb-2 mb-2 border-b border-gray-300">
                      <FiCreditCard className="text-indigo-600" size={14} />
                      <h3 className="font-semibold text-black text-sm">Payment Method</h3>
                    </div>
                    <div className="space-y-1.5">
                      {paymentMethods.map(method => (
                        <label 
                          key={method.value} 
                          className={`flex items-center p-1.5 rounded-lg border cursor-pointer transition-all text-xs ${
                            paymentMethod === method.value 
                              ? method.color + ' border-opacity-100' 
                              : 'bg-white border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          <input
                            type="radio"
                            name="paymentMethod"
                            value={method.value}
                            checked={paymentMethod === method.value}
                            onChange={(e) => setPaymentMethod(e.target.value)}
                            className="sr-only"
                          />
                          <span className="text-lg mr-2">{method.icon}</span>
                          <span className="font-medium text-xs">{method.label}</span>
                          {paymentMethod === method.value && (
                            <FiCheck className="ml-auto text-green-600" size={14} />
                          )}
                        </label>
                      ))}
                    </div>
                  </div>
    
                  {/* Payment Summary Card */}
                  <div className="bg-gray-100 rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center gap-2 pb-2 mb-2 border-b border-gray-300">
                      <FiDollarSign className="text-indigo-600" size={14} />
                      <h3 className="font-semibold text-black text-sm">Payment Summary</h3>
                    </div>
                    <div className="space-y-1.5">
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-black">Bills</span>
                        <span className="font-semibold text-black">{selectedBills.length}</span>
                      </div>
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-black">Total Due</span>
                        <span className="font-semibold text-black">₹{calculateTotal().toLocaleString()}</span>
                      </div>
                      <div className="border-t border-gray-300 pt-1.5 mt-1.5">
                        <div className="flex justify-between items-center">
                          <span className="text-xs font-medium text-black">Payable</span>
                          <span className="font-bold text-base text-indigo-600">₹{calculatePaymentTotal().toLocaleString()}</span>
                        </div>
                      </div>

                      {/* Paying Amount Input */}
                      {paymentMethod === 'Cash' && (
                        <div className="mt-2">
                          <label className="block text-xs font-medium text-black mb-1">
                            Paying Amount
                          </label>
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-black text-xs">₹</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={cashReceived}
                              onChange={(e) => setCashReceived(parseFloat(e.target.value) || 0)}
                              className="w-full pl-6 pr-2 py-1.5 border border-gray-400 rounded-lg focus:ring-1 focus:ring-indigo-200 focus:border-indigo-500 text-sm font-medium bg-white"
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                      )}

                      {/* Cash Payment Summary */}
                      {paymentMethod === 'Cash' && cashReceived > 0 && (
                        <div className="border-t border-gray-300 pt-1.5 mt-1.5">
                          <div className="flex justify-between items-center text-xs">
                            <span className="font-medium text-black">
                              {calculateBalance() >= 0 ? 'Return' : 'Short'}
                            </span>
                            <span className={`font-bold text-sm ${
                              calculateBalance() >= 0 ? 'text-red-600' : 'text-red-600'
                            }`}>
                              ₹{Math.abs(calculateBalance()).toLocaleString()}
                            </span>
                          </div>
                          {calculateBalance() > 0 && (
                            <div className="mt-1.5 p-1.5 bg-orange-50 border border-orange-200 rounded text-xs text-red-700 text-center font-medium">
                              Return ₹{calculateBalance().toLocaleString()}
                            </div>
                          )}
                          {calculateBalance() < 0 && (
                            <div className="mt-1.5 p-1.5 bg-red-50 border border-red-200 rounded text-xs text-red-700 text-center font-medium">
                              Need ₹{Math.abs(calculateBalance()).toLocaleString()} more
                            </div>
                          )}
                        </div>
                      )}

                      <div className="flex justify-between items-center text-xs pt-1.5 border-t border-gray-300 mt-1.5">
                        <span className="text-black">Remaining</span>
                        <span className="font-semibold text-red-600">₹{(calculateTotal() - calculatePaymentTotal()).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
    
                {/* Right Column - Bills List */}
                <div className="lg:col-span-2">
                  <div className="bg-gray-100 rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center gap-2 pb-2 mb-2 border-b border-gray-300">
                      <FiFileText className="text-indigo-600" size={14} />
                      <h3 className="font-semibold text-black text-sm">Selected Bills ({selectedBills.length})</h3>
                    </div>
                    
                    <div className="space-y-2.5 max-h-[calc(95vh-280px)] overflow-y-auto">
                      {selectedBills.map(billId => {
                        const bill = allBills.find(b => b.id === billId);
                        const payingAmount = paymentAmounts[billId] || 0;
                        const remainingAmount = bill ? bill.amount - payingAmount : 0;
                        const receiptHistory = getReceiptHistory(billId);
                        
                        return bill ? (
                          <div key={billId} className="bg-white rounded-lg border border-gray-300 p-2.5 hover:shadow-sm transition-shadow">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                              <div>
                                <div className="flex items-center justify-between mb-1.5">
                                  <h4 className="font-semibold text-black text-xs">{bill.patientName}</h4>
                                  <span className="text-xs bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">
                                    {bill.opNumber}
                                  </span>
                                </div>
                                <div className="text-xs text-black space-y-0.5">
                                  <div>Invoice: #{bill.invoiceNo}</div>
                                  <div>Dept: {bill.department}</div>
                                  <div className="flex justify-between pt-0.5">
                                    <span>Due:</span>
                                    <span className="font-semibold">₹{bill.amount.toLocaleString()}</span>
                                  </div>
                                  {bill.paidAmount > 0 && (
                                    <div className="flex justify-between text-green-600">
                                      <span className="text-black">Paid:</span>
                                      <span className="font-semibold text-black">₹{bill.paidAmount.toLocaleString()}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              <div className="space-y-2">
                                <div>
                                  <label className="block text-xs font-medium text-black mb-1">
                                    Paying Amount
                                  </label>
                                  <div className="relative">
                                    <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs">₹</span>
                                    <input
                                      type="number"
                                      min="0"
                                      max={bill.amount}
                                      step="0.01"
                                      value={payingAmount}
                                      onChange={(e) => updatePaymentAmount(billId, e.target.value)}
                                      className="w-full pl-6 pr-2 py-1.5 border border-gray-400 rounded-lg focus:ring-1 focus:ring-indigo-200 focus:border-indigo-500 text-sm bg-white"
                                      placeholder="0.00"
                                    />
                                  </div>
                                </div>
                                
                                <div className="flex justify-between text-xs">
                                  <span className="text-black">Remaining:</span>
                                  <span className={`font-semibold ${remainingAmount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                    ₹{remainingAmount.toLocaleString()}
                                  </span>
                                </div>
                                
                                <div className="flex gap-1.5">
                                  <button
                                    onClick={() => updatePaymentAmount(billId, bill.amount)}
                                    className="flex-1 px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                                  >
                                    Full
                                  </button>
                                  <button
                                    onClick={() => updatePaymentAmount(billId, bill.amount / 2)}
                                    className="flex-1 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                                  >
                                    Half
                                  </button>
                                </div>
                              </div>
                            </div>

                            {/* Receipt History Section */}
                            {receiptHistory && receiptHistory.length > 0 && (
                              <div className="mt-2.5 pt-2.5 border-t border-gray-200">
                                <div className="flex items-center mb-2">
                                  <FiClock className="mr-1.5 text-indigo-600" size={12} />
                                  <h5 className="text-xs font-semibold text-black">Payment History</h5>
                                  <span className="ml-1.5 text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">
                                    {receiptHistory.length}
                                  </span>
                                </div>
                                
                                <div className="space-y-1.5 max-h-32 overflow-y-auto">
                                  {receiptHistory.map((receipt, index) => (
                                    <div key={index} className="bg-gray-50 rounded p-2 border border-gray-200">
                                      <div className="flex items-center justify-between mb-1">
                                        <div className="flex items-center gap-1.5">
                                          <span className="text-sm font-bold text-black">
                                            ₹{receipt.PayingAmout?.toLocaleString() || 0}
                                          </span>
                                          <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${getPaymentBadgeColor(receipt.PaymentMode)}`}>
                                            {receipt.PaymentMode}
                                          </span>
                                        </div>
                                        <span className="text-xs bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded font-medium">
                                          #{receipt.ReceiptNo}
                                        </span>
                                      </div>
                                      <div className="flex items-center justify-between text-xs text-gray-600 pt-0.5 border-t border-gray-200">
                                        <span className="text-black">{receipt.UserName}</span>
                                        <span className="text-black">
                                          {formatDateTime(receipt.CreatedDate)}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>

                                {/* Total Paid Summary */}
                                <div className="mt-2 pt-2 border-t border-gray-200">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-medium text-black">Total Paid:</span>
                                    <span className="text-sm font-bold text-green-600">
                                      ₹{receiptHistory.reduce((sum, r) => sum + (r.PayingAmout || 0), 0).toLocaleString()}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        ) : null;
                      })}
                    </div>
                  </div>
                </div>
              </div>
    
              {/* Footer Actions */}
              <div className="mt-3 flex justify-between items-center pt-3 border-t border-gray-300 bg-white p-3">
                <div className="flex items-center gap-3">
                  <div className="text-xs text-black">
                    <span className="font-medium">{paymentMethods.find(m => m.value === paymentMethod)?.label}</span>
                  </div>
                  <label className="inline-flex items-center">
                    <input 
                      type="checkbox" 
                      checked={printAfterSave} 
                      onChange={(e) => setPrintAfterSave(e.target.checked)}
                      className="text-indigo-600 focus:ring-indigo-500 h-3.5 w-3.5"
                    />
                    <span className="ml-1.5 text-xs text-black">Print receipt</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input 
                      type="checkbox" 
                      checked={printBillAfterSave} 
                      onChange={(e) => setPrintBillAfterSave(e.target.checked)}
                      className="text-indigo-600 focus:ring-indigo-500 h-3.5 w-3.5"
                    />
                    <span className="ml-1.5 text-xs text-black">Print bill</span>
                  </label>
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setShowPaymentModal(false);
                      setPaymentAmounts({});
                      setCashReceived(0);
                      setBillsWithHistory({});
                    }}
                    className="px-4 py-1.5 text-sm text-gray-700 border border-gray-400 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handlePayment}
                    className="px-5 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:shadow-md transition-all text-sm font-medium flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiCheck size={16} />
                    <span>Process ₹{calculatePaymentTotal().toLocaleString()}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

export default PaymentModal;